// Menu do módulo CRM.
//
// Só os itens que têm tela: painel, leads, funil, negócios, atividades, empresas e configurações.
// Configurações entrou quando os endpoints de escrita passaram a existir (funil, times, campos
// customizados e cadências) — antes disso ficava de fora, porque item de menu que abre 404 é pior
// que item ausente.
//
// A ordem é a do dia de trabalho, não a do organograma: o painel abre a leitura, leads/funil/
// negócios são o fluxo da venda, atividades é o que fazer agora e empresas é a consulta.
//
// As permissões saem de `src/Rocky.Modules.CRM/Services/CrmPermissoes.cs` — o menu filtra por
// elas, mas quem realmente barra é o endpoint; item escondido é conveniência, não segurança.

import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import FundProjectionScreenOutlined from '@ant-design/icons/FundProjectionScreenOutlined';
import AppstoreOutlined from '@ant-design/icons/AppstoreOutlined';
import PieChartOutlined from '@ant-design/icons/PieChartOutlined';
import ScheduleOutlined from '@ant-design/icons/ScheduleOutlined';
import BankOutlined from '@ant-design/icons/BankOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';
import WhatsAppOutlined from '@ant-design/icons/WhatsAppOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import LineChartOutlined from '@ant-design/icons/LineChartOutlined';
import AimOutlined from '@ant-design/icons/AimOutlined';
import FunnelPlotOutlined from '@ant-design/icons/FunnelPlotOutlined';
import ApartmentOutlined from '@ant-design/icons/ApartmentOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import TeamOutlined from '@ant-design/icons/TeamOutlined';
import RiseOutlined from '@ant-design/icons/RiseOutlined';
import BuildOutlined from '@ant-design/icons/BuildOutlined';
import BellOutlined from '@ant-design/icons/BellOutlined';

import { NavItemType } from 'types/menu';

const crm: NavItemType = {
  id: 'group-crm',
  title: 'CRM',
  type: 'group',
  children: [
    {
      id: 'back-to-core-crm',
      title: 'Voltar ao Principal',
      type: 'item',
      url: '/dashboard',
      icon: ArrowLeftOutlined,
      breadcrumbs: false
    },
    {
      id: 'crm-leads',
      title: 'Leads',
      type: 'item',
      url: '/crm/leads',
      icon: UserAddOutlined,
      permissions: ['crm.lead.view'],
      breadcrumbs: false
    },
    {
      // Mesmo dado de "Negócios", agrupado por estágio. Por isso compartilha a permissão.
      id: 'crm-funil',
      title: 'Funil',
      type: 'item',
      url: '/crm/funil',
      icon: AppstoreOutlined,
      permissions: ['crm.negocio.view'],
      breadcrumbs: false
    },
    {
      id: 'crm-negocios',
      title: 'Negócios',
      type: 'item',
      url: '/crm/negocios',
      icon: FundProjectionScreenOutlined,
      permissions: ['crm.negocio.view'],
      breadcrumbs: false
    },
    {
      id: 'crm-atividades',
      title: 'Atividades',
      type: 'item',
      url: '/crm/atividades',
      icon: ScheduleOutlined,
      permissions: ['crm.atividade.view'],
      breadcrumbs: false
    },
    {
      // Ler a caixa é crm.atividade.view (a mesma permissão da atividade que a conversa gera);
      // falar é crm.comunicacao.enviar, e é a tela que esconde o compositor de quem não tem.
      id: 'crm-whatsapp',
      title: 'WhatsApp',
      type: 'item',
      url: '/crm/whatsapp',
      icon: WhatsAppOutlined,
      permissions: ['crm.atividade.view'],
      breadcrumbs: false
    },
    {
      // A ficha 360 não tem item próprio: entra-se nela pela lista de empresas (ou por um link
      // de outra tela). Item de menu para uma rota que exige um id seria item quebrado.
      id: 'crm-empresas',
      title: 'Empresas',
      type: 'item',
      url: '/crm/empresas',
      icon: BankOutlined,
      permissions: ['crm.empresa.view'],
      breadcrumbs: false
    },
    {
      // Fila de revisão de duplicados. Permissão de VER lead: quem limpa a base é quem trabalha
      // nela — mesclar, que é irreversível, exige crm.atribuir e o serviço confere.
      id: 'crm-duplicados',
      title: 'Duplicados',
      type: 'item',
      url: '/crm/duplicados',
      icon: CopyOutlined,
      permissions: ['crm.lead.view'],
      breadcrumbs: false
    },
    {
      // Colapsado: são telas de olhar, não de trabalhar — abertas uma vez por dia ou por semana.
      // Os três painéis ficam juntos e na ordem do público (vendedor, gestor, diretoria), que é a
      // pergunta que cada um responde; antes estavam espalhados e o nome não dizia de quem eram.
      id: 'crm-analise',
      title: 'Análise',
      type: 'collapse',
      icon: PieChartOutlined,
      children: [
        {
          // Mesma permissão do Painel acima: o que muda entre os três é a PERGUNTA da tela, não a
          // alçada. Quem enxerga o quê continua sendo escopo — abrir o do gestor sem time embaixo
          // traz a própria carteira no layout do gestor.
          id: 'crm-painel-vendedor',
          title: 'Meu dia',
          type: 'item',
          url: '/crm/painel/vendedor',
          icon: UserOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-painel-gestor',
          title: 'Meu time',
          type: 'item',
          url: '/crm/painel/gestor',
          icon: TeamOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-painel-diretoria',
          title: 'Diretoria',
          type: 'item',
          url: '/crm/painel/diretoria',
          icon: RiseOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          // Permissão própria: ver o painel é separado de ver negócio (`crm.relatorio.view`).
          id: 'crm-dashboard',
          title: 'Painel',
          type: 'item',
          url: '/crm/dashboard',
          icon: PieChartOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-forecast',
          title: 'Forecast',
          type: 'item',
          url: '/crm/forecast',
          icon: LineChartOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-metas',
          title: 'Metas',
          type: 'item',
          url: '/crm/metas',
          icon: AimOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-rel-funil',
          title: 'Relatório de funil',
          type: 'item',
          url: '/crm/relatorios/funil',
          icon: FunnelPlotOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-rel-atividade',
          title: 'Relatório de atividade',
          type: 'item',
          url: '/crm/relatorios/atividade',
          icon: ScheduleOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-rel-origem',
          title: 'Relatório de origem',
          type: 'item',
          url: '/crm/relatorios/origem',
          icon: ApartmentOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        },
        {
          id: 'crm-construtor',
          title: 'Construtor de relatório',
          type: 'item',
          url: '/crm/construtor',
          icon: BuildOutlined,
          permissions: ['crm.relatorio.view'],
          breadcrumbs: false
        }
      ]
    },
    {
      // Webhooks de saída estava ACIMA de Leads: uma tela de incidente, aberta duas vezes por ano,
      // treze posições acima da fila que o vendedor trabalha o dia inteiro. A ordem do menu era a
      // ordem em que as telas foram construídas, não a de uso.
      id: 'crm-config',
      title: 'Configuração',
      type: 'collapse',
      icon: SettingOutlined,
      children: [
        {
          // Duas permissões porque são duas alçadas: desenhar o processo (funil, campos, cadências) é
          // `crm.config.manage`; administrar quem enxerga o funil de quem é `crm.time.manage`. O menu
          // filtra por OR — quem tem uma das duas entra e vê só a aba que lhe cabe.
          id: 'crm-configuracoes',
          title: 'Configurações',
          type: 'item',
          url: '/crm/configuracoes',
          icon: SettingOutlined,
          permissions: ['crm.config.manage', 'crm.time.manage'],
          breadcrumbs: false
        },
        // "Webhooks de saída" saiu daqui: o motor virou da casa (módulo Integrações, rotas
        // `/api/admin/webhooks…`) e a tela mora em Administração → Webhooks de saída, sob
        // `admin.webhooks.manage`. O catálogo de eventos assináveis agora mistura CRM e Helpdesk —
        // mantê-la dentro de "Configuração do CRM" diria que a assinatura é do CRM, e não é.
        {
          // Item próprio e não aba de Configurações: aquela tela é o desenho do processo
          // (`crm.config.manage`), esta é a conta da pessoa — e quem mais precisa dela é exatamente
          // quem não tem aquelas permissões.
          id: 'crm-notificacoes',
          title: 'Minhas notificações',
          type: 'item',
          url: '/crm/notificacoes',
          icon: BellOutlined,
          permissions: ['crm.acessar'],
          breadcrumbs: false
        }
      ]
    }
  ]
};

export default crm;
